import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Spin,
  Table,
  message,
} from 'antd';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import PageTitle from 'apps/home/components/PageTitle';
import stockProductsApiCall from 'apps/investcore/apiCalls/stock-products';
import {
  createStagPortfolioApiCall,
  deleteStagPortfolioApiCall,
  getStagPortfolioApiCall,
  listStagPortfoliosApiCall,
  updateStagPortfolioApiCall,
} from 'apps/investcore/apiCalls/stag-portfolios';
import ErrorMessage from 'common/ui/ErrorMessage';
import LoadingSpinner from 'common/ui/LoadingSpinner';
import { useFetchApiList } from 'hooks/api';

const TOTAL_TOLERANCE = 0.1;
const debounceTimeout = 500;

const RISK_PROFILE_OPTIONS = [
  { value: 1001, label: 'Rất thận trọng' },
  { value: 1002, label: 'Thận trọng' },
  { value: 1003, label: 'Cân bằng' },
  { value: 1004, label: 'Mạo hiểm' },
  { value: 1005, label: 'Rất mạo hiểm' },
];

function riskProfileLabel(value) {
  return RISK_PROFILE_OPTIONS.find(o => o.value === value)?.label || '—';
}

export const StagPortfolioListPage = () => {
  const queryClient = useQueryClient();

  const { data: response, isLoading, error: fetchError } = useQuery({
    queryKey: [listStagPortfoliosApiCall.queryKey],
    queryFn: () => listStagPortfoliosApiCall.queryFn(undefined),
  });

  const { mutate: remove } = useMutation({
    mutationFn: id => deleteStagPortfolioApiCall.queryFn({ id }),
    onSuccess: () => {
      message.success('Đã xoá danh mục mẫu');
      queryClient.invalidateQueries({ queryKey: [listStagPortfoliosApiCall.queryKey] });
    },
    onError: err => {
      message.error(err?.message || 'Xoá danh mục mẫu thất bại');
    },
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (fetchError) {
    return <ErrorMessage {...fetchError} />;
  }

  const items = response?.data || [];

  const columns = [
    {
      title: 'Tên',
      dataIndex: 'title',
      key: 'title',
      render: (value, record) => (
        <Link to={`/admin/stag-portfolios/${record.id}`}>{value}</Link>
      ),
    },
    {
      title: 'Khẩu vị rủi ro',
      dataIndex: 'risk_profile',
      key: 'risk_profile',
      render: value => riskProfileLabel(value),
    },
    {
      title: 'Số mã CK',
      key: 'items',
      render: (_, record) => (record.items || []).length,
    },
    {
      title: 'Tổng tỷ trọng',
      dataIndex: 'total_percentage',
      key: 'total_percentage',
      render: value => `${(value || 0).toFixed(2)}%`,
    },
    { title: 'Thứ tự', dataIndex: 'ranking', key: 'ranking' },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <Link to={`/admin/stag-portfolios/${record.id}`}>Xem</Link>
          <Link to={`/admin/stag-portfolios/${record.id}/edit`}>Sửa</Link>
          <Popconfirm
            title="Xoá danh mục mẫu này?"
            onConfirm={() => remove(record.id)}
          >
            <DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer' }} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageTitle
        title="Stag Portfolios"
        backUrl="/admin"
        extra={
          <Link to="/admin/stag-portfolios/create">
            <Button type="primary" icon={<PlusOutlined />}>
              Thêm danh mục mẫu
            </Button>
          </Link>
        }
      />
      <p className="mb-4 text-gray-500">
        Danh mục mẫu (Tăng trưởng, Cân bằng, Thận trọng...) giúp cố vấn thiết lập nhanh
        danh mục mục tiêu cho khách hàng.
      </p>
      <Table rowKey="id" columns={columns} dataSource={items} pagination={false} />
    </>
  );
};

export const StagPortfolioFormPage = () => {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: response, isLoading, error: fetchError } = useQuery({
    queryKey: [getStagPortfolioApiCall.queryKey, id],
    queryFn: () => getStagPortfolioApiCall.queryFn({ id }),
    enabled: !isNew,
  });

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [riskProfile, setRiskProfile] = useState(1003);
  const [ranking, setRanking] = useState(0);
  const [rows, setRows] = useState([]);
  const [initialized, setInitialized] = useState(isNew);

  useEffect(() => {
    if (response?.data && !initialized) {
      const item = response.data;
      setTitle(item.title || '');
      setDescription(item.description || '');
      setRiskProfile(item.risk_profile || 1003);
      setRanking(item.ranking || 0);
      setRows(
        (item.items || []).map(row => ({
          stock_id: row.stock_id,
          symbol: row.symbol,
          name: row.name,
          target_percentage: row.target_percentage,
        }))
      );
      setInitialized(true);
    }
  }, [response, initialized]);

  const {
    data: stocks = [],
    load: loadStocks,
    isLoading: isLoadingStocks,
  } = useFetchApiList(stockProductsApiCall.list, { resourceName: 'data' });

  // stockProductsApiCall defaults to type=stock (it backs other investcore
  // pickers that are stock-only) — override with type: undefined so ETFs are
  // included here too, since a Stag Portfolio can target either.
  useEffect(() => {
    loadStocks({ type: undefined, page_size: 20 });
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce(
        value => loadStocks({ type: undefined, search: value, page_size: 20 }),
        debounceTimeout
      ),
    [loadStocks]
  );

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: () => {
      const payload = {
        title,
        description,
        risk_profile: riskProfile,
        ranking,
        items: rows.map(r => ({ stock_id: r.stock_id, target_percentage: r.target_percentage })),
      };
      return isNew
        ? createStagPortfolioApiCall.queryFn(payload)
        : updateStagPortfolioApiCall.queryFn({ id, ...payload });
    },
    onSuccess: () => {
      message.success('Đã lưu danh mục mẫu');
      queryClient.invalidateQueries({ queryKey: [listStagPortfoliosApiCall.queryKey] });
      navigate('/admin/stag-portfolios');
    },
    onError: err => {
      message.error(err?.message || 'Lưu danh mục mẫu thất bại');
    },
  });

  const { mutate: remove, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteStagPortfolioApiCall.queryFn({ id }),
    onSuccess: () => {
      message.success('Đã xoá danh mục mẫu');
      queryClient.invalidateQueries({ queryKey: [listStagPortfoliosApiCall.queryKey] });
      navigate('/admin/stag-portfolios');
    },
    onError: err => {
      message.error(err?.message || 'Xoá danh mục mẫu thất bại');
    },
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (fetchError) {
    return <ErrorMessage {...fetchError} />;
  }

  const existingStockIds = rows.map(r => r.stock_id);
  const availableStocks = stocks.filter(s => !existingStockIds.includes(s.id));
  const total = rows.reduce((sum, r) => sum + (r.target_percentage || 0), 0);
  const isTotalValid = rows.length === 0 || Math.abs(total - 100) <= TOTAL_TOLERANCE;
  const hasInvalidRow = rows.some(r => !r.target_percentage || r.target_percentage <= 0);
  const canSave = !!title && isTotalValid && !hasInvalidRow;

  const addStock = stockId => {
    const stock = stocks.find(s => s.id === stockId);
    if (!stock) return;
    setRows(prev => [
      ...prev,
      { stock_id: stock.id, symbol: stock.symbol, name: stock.name, target_percentage: 0 },
    ]);
  };

  const updateTarget = (stockId, value) => {
    setRows(prev =>
      prev.map(r => (r.stock_id === stockId ? { ...r, target_percentage: value || 0 } : r))
    );
  };

  const removeRow = stockId => {
    setRows(prev => prev.filter(r => r.stock_id !== stockId));
  };

  return (
    <>
      <PageTitle
        title={isNew ? 'Thêm danh mục mẫu' : 'Sửa danh mục mẫu'}
        backUrl="/admin/stag-portfolios"
      />

      <div className="my-4 grid max-w-xl grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="mb-1 block text-sm text-gray-500">Tên danh mục</label>
          <Input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="VD: Danh mục Tăng trưởng"
          />
        </div>
        <div className="col-span-2">
          <label className="mb-1 block text-sm text-gray-500">Mô tả</label>
          <Input.TextArea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={2}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-gray-500">Khẩu vị rủi ro</label>
          <Select
            value={riskProfile}
            onChange={setRiskProfile}
            options={RISK_PROFILE_OPTIONS}
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-gray-500">Thứ tự hiển thị</label>
          <InputNumber value={ranking} onChange={v => setRanking(v || 0)} style={{ width: '100%' }} />
        </div>
      </div>

      <div className="my-4 flex items-center gap-2">
        <Select
          showSearch
          allowClear
          value={null}
          placeholder="Tìm mã cổ phiếu để thêm"
          style={{ width: 320 }}
          filterOption={false}
          onSearch={debouncedSearch}
          onChange={addStock}
          notFoundContent={isLoadingStocks ? <Spin size="small" /> : null}
          options={availableStocks.map(s => ({
            value: s.id,
            label: `${s.symbol} - ${s.name}`,
          }))}
        />
      </div>

      {rows.length === 0 ? (
        <p className="py-6 text-center text-gray-500">
          Chưa có mã cổ phiếu nào. Thêm mã cổ phiếu để bắt đầu.
        </p>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b text-left text-sm text-gray-500">
              <th className="py-2">Mã CK</th>
              <th className="py-2">Tên</th>
              <th className="py-2 text-right">Tỷ trọng (%)</th>
              <th className="py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.stock_id} className="border-b">
                <td className="py-2 font-medium">{row.symbol}</td>
                <td className="py-2 text-gray-500">{row.name}</td>
                <td className="py-2 text-right">
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.1}
                    precision={2}
                    value={row.target_percentage}
                    onChange={value => updateTarget(row.stock_id, value)}
                    addonAfter="%"
                    style={{ width: 140 }}
                  />
                </td>
                <td className="py-2 text-center">
                  <DeleteOutlined
                    style={{ color: '#ff4d4f', cursor: 'pointer' }}
                    onClick={() => removeRow(row.stock_id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="my-4 flex items-center justify-between rounded-md border px-4 py-3">
        <span className="text-gray-500">Tổng cộng</span>
        <span className={isTotalValid ? 'font-semibold text-green-600' : 'font-semibold text-red-500'}>
          {total.toFixed(2)}%
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Button type="primary" disabled={!canSave} loading={isSaving} onClick={() => save()}>
          Lưu danh mục mẫu
        </Button>
        {!isNew && (
          <Popconfirm title="Xoá danh mục mẫu này?" onConfirm={() => remove()}>
            <Button danger loading={isDeleting}>
              Xoá
            </Button>
          </Popconfirm>
        )}
      </div>
    </>
  );
};

export const StagPortfolioDetailPage = () => {
  const { id } = useParams();

  const { data: response, isLoading, error: fetchError } = useQuery({
    queryKey: [getStagPortfolioApiCall.queryKey, id],
    queryFn: () => getStagPortfolioApiCall.queryFn({ id }),
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (fetchError) {
    return <ErrorMessage {...fetchError} />;
  }

  const item = response?.data;

  if (!item) {
    return <ErrorMessage errors={{ message: 'Không tìm thấy danh mục mẫu' }} />;
  }

  const columns = [
    { title: 'Mã CK', dataIndex: 'symbol', key: 'symbol' },
    { title: 'Tên', dataIndex: 'name', key: 'name' },
    {
      title: 'Tỷ trọng mục tiêu',
      dataIndex: 'target_percentage',
      key: 'target_percentage',
      align: 'right' as const,
      render: value => `${(value || 0).toFixed(2)}%`,
    },
  ];

  return (
    <>
      <PageTitle
        title={item.title}
        backUrl="/admin/stag-portfolios"
        extra={
          <Link to={`/admin/stag-portfolios/${id}/edit`}>
            <Button type="primary">Sửa</Button>
          </Link>
        }
      />

      <div className="mb-6 grid max-w-xl grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-gray-500">Khẩu vị rủi ro</p>
          <p className="font-medium">{riskProfileLabel(item.risk_profile)}</p>
        </div>
        <div>
          <p className="text-gray-500">Thứ tự hiển thị</p>
          <p className="font-medium">{item.ranking}</p>
        </div>
        <div className="col-span-2">
          <p className="text-gray-500">Mô tả</p>
          <p className="font-medium">{item.description || '—'}</p>
        </div>
        <div className="col-span-2">
          <p className="text-gray-500">Tổng tỷ trọng</p>
          <p
            className={
              Math.abs((item.total_percentage || 0) - 100) <= TOTAL_TOLERANCE
                ? 'font-semibold text-green-600'
                : 'font-semibold text-red-500'
            }
          >
            {(item.total_percentage || 0).toFixed(2)}%
          </p>
        </div>
      </div>

      <Table
        rowKey="stock_id"
        columns={columns}
        dataSource={item.items || []}
        pagination={false}
      />
    </>
  );
};
